import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PlayerModal from '@/components/feature/PlayerModal';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { formatDuration, getMovieById, getGenreByKey, getRecommendations } from '@/utils/movies';

export default function Detail() {
  const { id = '' } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [toast, setToast] = useState('');

  useSeo({
    title: movie ? `电影《${movie.title}》在线观看 - 6080yy电影在线看` : '影片不存在 - 6080yy电影在线看',
    description: movie
      ? `电影《${movie.title}》（${movie.year}）${movie.genres.join('、')}，由${movie.director}执导，${movie.actors.slice(0, 3).join('、')}主演。剧情简介：${movie.synopsis.slice(0, 60)}…6080yy电影在线看提供高清在线观看。`
      : '您访问的影片不存在或已被移除。',
    keywords: movie ? `${movie.title},${movie.title}在线观看,${movie.genres.join(',')},6080yy电影在线看` : '6080yy电影在线看',
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，快分享给好友吧');
    } catch {
      showToast('分享功能为演示效果，暂未接入真实分享');
    }
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-6 py-16 text-center">
        <i className="ri-film-line text-5xl text-foreground-300 w-14 h-14 flex items-center justify-center mx-auto"></i>
        <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">影片不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的影片可能已下架或链接有误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const recommendations = getRecommendations(movie, 6);

  return (
    <div className="w-full">
      <div className="relative w-full bg-primary-950">
        <img src={movie.backdrop} alt={`${movie.title} 背景图`} className="absolute inset-0 w-full h-full object-cover object-top opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70"></div>
        <div className="relative w-full px-4 md:px-6 py-6 md:py-8">
          <nav className="flex items-center gap-1.5 text-sm text-background-200/80 mb-5 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-background-200/50"></i>
            <span>电影详情</span>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-background-200/50"></i>
            <span className="text-background-50">{movie.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-[180px] sm:w-[220px] md:w-[260px] shrink-0 mx-auto md:mx-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-primary-700">
                <img
                  src={movie.poster}
                  alt={`${movie.title}（${movie.year}）高清海报`}
                  title={`${movie.title} - 在线观看 - 6080yy电影在线看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-background-50">{movie.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1 text-accent-400 font-heading font-bold text-lg">
                  <i className="ri-star-fill w-5 h-5 flex items-center justify-center"></i>
                  {movie.rating.toFixed(1)}
                </span>
                <span>{movie.year}</span>
                <span>{formatDuration(movie.duration)}</span>
                <span>{movie.region}</span>
                <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-xs font-label font-semibold">
                  {movie.status}
                </span>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <p className="text-background-200">
                  <span className="text-background-200/60">导演：</span>
                  <span className="text-background-50">{movie.director}</span>
                </p>
                <p className="text-background-200">
                  <span className="text-background-200/60">主演：</span>
                  <span className="text-background-50">{movie.actors.join(' / ')}</span>
                </p>
                <div className="flex items-start gap-2 flex-wrap pt-1">
                  <span className="text-background-200/60">类型：</span>
                  <div className="flex flex-wrap gap-2">
                    {movie.genres.map((genre, i) => {
                      const key = movie.genreKeys[i];
                      return (
                        <Link
                          key={genre}
                          to={`/category/${key}`}
                          className="px-2.5 py-0.5 rounded-full bg-background-50/10 text-background-100 text-xs hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
                        >
                          {genre}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayerOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-primary-950 font-label font-semibold text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill w-5 h-5 flex items-center justify-center"></i>
                  在线观看
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => showToast('微信分享为演示效果，请复制链接后分享')}
                    aria-label="分享到微信"
                    className="w-10 h-10 rounded-md border border-background-50/25 text-background-50 hover:bg-background-50/10 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-wechat-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => showToast('微博分享为演示效果，请复制链接后分享')}
                    aria-label="分享到微博"
                    className="w-10 h-10 rounded-md border border-background-50/25 text-background-50 hover:bg-background-50/10 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-weibo-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    aria-label="复制链接"
                    className="w-10 h-10 rounded-md border border-background-50/25 text-background-50 hover:bg-background-50/10 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-link-m w-5 h-5 flex items-center justify-center"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-3">
              <span>剧情简介</span>
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-loose">{movie.synopsis}</p>

            <div className="mt-8">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-3">
                <span>影片标签</span>
              </h2>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-sm font-label">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside>
            <div className="bg-background-50 rounded-lg border border-background-200 p-5">
              <h2 className="font-heading font-bold text-base text-foreground-950 mb-4">
                <span>影片信息</span>
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500 shrink-0">上映年份</dt>
                  <dd className="text-foreground-900 text-right">{movie.year}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500 shrink-0">影片时长</dt>
                  <dd className="text-foreground-900 text-right">{formatDuration(movie.duration)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500 shrink-0">制片地区</dt>
                  <dd className="text-foreground-900 text-right">{movie.region}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500 shrink-0">影片状态</dt>
                  <dd className="text-foreground-900 text-right">{movie.status}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500 shrink-0">综合评分</dt>
                  <dd className="text-accent-600 font-heading font-bold text-right">{movie.rating.toFixed(1)}</dd>
                </div>
              </dl>
              <div className="mt-5 pt-4 border-t border-background-200">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  <i className="ri-information-line w-4 h-4 inline-flex items-center justify-center align-middle mr-1"></i>
                  播放功能为界面演示，暂未接入真实视频流。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-10 rounded-md bg-primary-500 flex items-center justify-center shrink-0">
              <i className="ri-movie-2-line text-accent-500 text-xl w-5 h-5 flex items-center justify-center"></i>
            </span>
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
              <span>相关推荐</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {recommendations.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </div>
      </div>

      <PlayerModal movie={movie} open={playerOpen} onClose={() => setPlayerOpen(false)} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[110] px-5 py-3 rounded-md bg-primary-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}